import dayjs, { Dayjs } from 'dayjs'
import { useEffect, useState } from 'react'

import { selectScenarioWithShiftsById } from '@/entities/schedules'
import { publishUnassignedOrders } from '@/entities/schedules/thunks/publishUnassignedOrders'
import { Button, Modal } from '@/shared/components'
import { DatePicker } from '@/shared/components/DatePicker/DatePicker'
import { DialogComponentProps } from '@/shared/components/Modal/models/DialogStructure'
import { TextField } from '@/shared/components/TextField/TextField'
import { useToastContext } from '@/shared/components/Toast/ToastContext'
import { useAppDispatch, useAppSelector } from '@/shared/store'
import { styled, Typography } from '@mui/material'

export type PublishUnassignedOrdersDialogProps = DialogComponentProps & {
  onClose: () => void
}

export const PublishUnassignedOrdersDialog: React.FC<PublishUnassignedOrdersDialogProps> = ({
  open,
  onClose,
  scenarioId,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [publishToDate, setPublishToDate] = useState<Dayjs | null>(null)
  const { setToastContent } = useToastContext()
  const scenario = useAppSelector(state => selectScenarioWithShiftsById(state, scenarioId))
  const dispatch = useAppDispatch()

  // Initialize Publish To Date
  useEffect(() => {
    if (scenario?.dateOfOperation) {
      setPublishToDate(dayjs(scenario.dateOfOperation, 'DD/MM/YYYY'))
    }
  }, [scenario])

  const onChangePublishToDate = (date: Dayjs | null) => {
    setPublishToDate(date)
  }

  // On Submit Form
  const onSubmit = async () => {
    if (!scenario) return
    setIsLoading(true)

    if (!scenario?.unassignedOrderCount || scenario.unassignedOrderCount === 0) {
      setToastContent({
        open: true,
        severity: 'error',
        title: 'No orders found',
        content: 'Scenario has no unassigned orders',
      })

      return
    }

    if (!publishToDate) {
      setToastContent({
        open: true,
        severity: 'error',
        title: 'Publish Date Required',
        content: 'Publish to date is required.',
      })
      setIsLoading(false)

      return
    }

    const publishDateOfWeek = publishToDate.day()

    if (publishDateOfWeek !== scenario.weekday) {
      setToastContent({
        open: true,
        severity: 'error',
        title: 'Invalid Publish Date',
        content: 'Publish to date must be the same day of the week as the scenario.',
      })
      setIsLoading(false)

      return
    }

    // Publish scenario with unassigned orders
    await dispatch(
      publishUnassignedOrders({
        id: scenario.id,
        dateOfOperation: publishToDate?.format('DD/MM/YYYY') || '',
        onSuccess: () => {
          setIsLoading(false)
          onClose()
        },
      }),
    )
    setIsLoading(false)
  }

  return (
    <Modal
      title={`Publish Unassigned Orders: ${scenario?.name}`}
      onClose={onClose}
      open={open}
      transition="opacity"
      showBackground={true}
      disableClose={isLoading}
    >
      <StyledContent>
        <StyledSectionTypography variant="body1">
          Publish {scenario?.unassignedOrderCount || 0} unassigned orders to
        </StyledSectionTypography>

        <TextField
          label="Scenario Name"
          value={scenario?.name}
          disabled
        />
        <TextField
          label="Day of Week"
          value={scenario?.dayOfWeek}
          disabled
        />
        <DatePicker
          label="Publish to"
          format="DD/MM/YYYY"
          onAccept={onChangePublishToDate}
          onChange={onChangePublishToDate}
          value={publishToDate}
          disablePast
          slotProps={{
            textField: {
              fullWidth: true,
              required: true,
            },
          }}
        />
        <StyledFormActions>
          {onClose && <Button text onClick={onClose}>Cancel</Button>}
          <Button
            primary
            onClick={onSubmit}
            disabled={!publishToDate}
          >
            Publish
          </Button>
        </StyledFormActions>
      </StyledContent>
    </Modal>
  )
}

const StyledContent = styled('div')`
  ${(props) => props.theme.breakpoints.up('sm')} {
    min-width: ${(props) => props.theme.typography.pxToRem(360)};
    max-width: ${(props) => props.theme.typography.pxToRem(460)};
  }
`

const StyledFormActions = styled('div')`
  width: 100%;
  padding-bottom: ${(props) => props.theme.typography.pxToRem(12)};
  display: flex;
  justify-content: flex-end;
  gap: ${(props) => props.theme.typography.pxToRem(12)};
  padding-top: ${(props) => props.theme.typography.pxToRem(8)};
  background-color: ${(props) => props.theme.palette.background.paper};
`

const StyledSectionTypography = styled(Typography)`
  font-weight: 500;
  font-size: ${(props) => props.theme.typography.pxToRem(16)};
  margin-bottom: ${(props) => props.theme.typography.pxToRem(8)};
`
